import React, { useCallback, useState } from 'react';
import { grey } from '@mui/material/colors/';
import {
	AppBar,
	Backdrop,
	Box,
	Button,
	CircularProgress,
	Divider,
	Drawer,
	IconButton,
	ImageList,
	ImageListItem,
	ImageListItemBar,
	Toolbar,
	Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ArrowBack, Close, InsertDriveFile, StopScreenShare } from '@mui/icons-material';
import { Auth } from 'aws-amplify';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import MD5 from 'crypto-js/md5';

import { DropZone } from '../../../common/components';
import {
	shareCanvasImgState,
	sharedAudiosState,
	sharedImageState,
	uploadedImagesState,
} from '../../../common/recoil/Share/atom';
import { addNewFile } from '../../../common/recoil/Share/selectors';
import { currentLibraryBreadCrumbState } from '../../../common/recoil/Library/libraryAtom';
import { currentLibraryContentSelector } from '../../../common/recoil/Library/librarySelector';
import { CHAT_EVENTS, LIBRARY_ITEMS_TYPES } from '../../../common/constants';
import { useChat } from '../../../common/hooks';
import { uploadFile } from '../../../common/services/libraryRequests';
import { findAndAddElement } from '../../../common/services/libraryUtils';

const useStyles = makeStyles((theme) => ({
	folderTree: {
		backgroundColor: grey[200],
		height: '220px',
		display: 'flex',
		flexWrap: 'wrap',
		justifyContent: 'space-around',
		overflow: 'hidden',
	},
	dropZone: {
		border: `dashed ${grey[500]}`,
		padding: '5px',
		margin: '5px',
		height: '200px',
	},
	imageList: {
		width: 500,
		height: 450,
	},
	fileIcon: {
		width: '50%',
		height: '50%',
	},
	backdrop: {
		zIndex: theme.zIndex.drawer + 1,
		color: '#fff',
	},
	stopShareImage: {
		position: 'absolute',
	},
	menuButton: {
		marginRight: theme.spacing(2),
	},
	backdrop: {
		zIndex: theme.zIndex.drawer + 1,
		color: '#fff',
	},
}));

const LibraryContent = () => {
	const classes = useStyles();
	const [uploadedImages, setUploadedImages] = useRecoilState(uploadedImagesState);
	const [sharedImage, setSharedImage] = useRecoilState(sharedImageState);
	const [sharedAudios, setSharedAudios] = useRecoilState(sharedAudiosState);
	const [shareCanvasImg, setShareCanvasImg] = useRecoilState(shareCanvasImgState);
	const [filesUploaded, setNewFile] = useRecoilState(addNewFile);
	const [uploadingFile, setUploadingFile] = useState(false);
	const { messages, sendMessage } = useChat();
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const [currentTab, setCurrentTab] = useState(2);
	const [currentLibraryContent, setCurrentLibraryContent] = useRecoilState(
		currentLibraryContentSelector
	);
	const currentLibraryBreadCrumb = useRecoilValue(currentLibraryBreadCrumbState);

	const { t, i18n } = useTranslation();

	const showError = (message) => {
		enqueueSnackbar(message, {
			variant: 'error',
			anchorOrigin: {
				vertical: 'bottom',
				horizontal: 'center',
			},
			action: (key) => (
				<Button onClick={onClickDismiss(key)}>
					<Close color={'white'}></Close>
				</Button>
			),
			preventDuplicate: true,
		});
	};

	const onClickDismiss = (key) => () => {
		closeSnackbar(key);
	};

	const onDrop = useCallback(
		async (file) => {
			const hash = MD5(file.binary).toString();
			try {
				setUploadingFile(true);
				const newFile = await uploadFile(file, hash);
				if (newFile) {
					newFile.hash = hash;
					setNewFile(newFile);
					addImageToFolder(newFile, file.name);
				} else {
					showError('Error');
				}
				setUploadingFile(false);
			} catch (e) {
				console.log('error', e);
				setUploadingFile(false);
			}
		},
		[currentLibraryContent, currentLibraryBreadCrumb]
	);

	const addImageToFolder = (file, name) => {
		let currentLibrary = { ...currentLibraryContent, dirty: true };
		currentLibrary.children = [...currentLibrary.children];
		currentLibrary = findAndAddElement(
			currentLibrary,
			currentLibraryBreadCrumb.slice(1),
			name,
			showError,
			LIBRARY_ITEMS_TYPES.FILE,
			file
		);
		setCurrentLibraryContent(currentLibrary);
	};

	const onImgClick = (img) => {
		if (img.isImage) {
			if (img.key === sharedImage.key) {
				stopShareImage();
			} else {
				setSharedImage(img);
				setShareCanvasImg(img.url);
				const shareImgMessage = {
					type: CHAT_EVENTS.SHARE_S3_IMAGE,
					img: img.url,
				};
				sendMessage(shareImgMessage);
			}
		} else if (img.isAudio) {
			const tempAudios = [...sharedAudios];
			if (!sharedAudios.some((x) => x.key === img.key)) {
				tempAudios.push(img);
				setSharedAudios(tempAudios);
				const shareImgMessage = {
					type: CHAT_EVENTS.SHARE_S3_AUDIO,
					sharedAudio: tempAudios,
				};
				sendMessage(shareImgMessage);
			}
		}
	};

	const stopShareImage = () => {
		setShareCanvasImg(null);
		setSharedImage(null);
		const shareImgMessage = {
			type: CHAT_EVENTS.SHARE_S3_IMAGE,
			img: null,
		};
		sendMessage(shareImgMessage);
	};

	return (
		<Box className={classes.folderTree}>
			<DropZone onFileReady={onDrop}></DropZone>

			<Backdrop className={classes.backdrop} open={uploadingFile}>
				<CircularProgress color="inherit" />
			</Backdrop>
		</Box>
	);
};

export default LibraryContent;
